/**
 * Порядок колонок при смене их набора.
 *
 * Порядок по умолчанию задаёт схема (порядок массива `columns`), но таблица
 * держит его состоянием — иначе не работали бы перетаскивание колонок и
 * сохранённые виды. Когда набор колонок меняется под живой таблицей (другая
 * папка поиска, другой предустановленный фильтр, другая схема), состояние
 * оставляло порядок прошлой схемы, и общие поля вставали на чужие места.
 */

/** Одинаковы ли списки полей по составу и порядку. */
export const sameFieldOrder = (a: readonly string[], b: readonly string[]): boolean =>
    a.length === b.length && a.every((field, index) => field === b[index]);

/**
 * Ручной порядок + новая схема. Поля, которых в схеме больше нет, уходят;
 * новые встают туда, где стоят в схеме — сразу за ближайшим соседом слева,
 * который уже размещён (а если такого нет — в конец).
 */
export function mergeColumnOrder(order: readonly string[], schema: readonly string[]): string[] {
    const known = new Set(schema);
    const result = order.filter((field) => known.has(field));
    const placed = new Set(result);

    schema.forEach((field, index) => {
        if (placed.has(field)) return;
        let at = result.length;
        for (let left = index - 1; left >= 0; left--) {
            const position = result.indexOf(schema[left]);
            if (position >= 0) {
                at = position + 1;
                break;
            }
        }
        result.splice(at, 0, field);
        placed.add(field);
    });

    return result;
}

/**
 * Следующий порядок колонок после смены схемы: пока человек колонки не
 * переставлял (порядок совпадает с прежней схемой), он следует схеме; ручной
 * порядок сохраняется и лишь дополняется новыми полями.
 */
export function nextColumnOrder(
    current: readonly string[],
    prevSchema: readonly string[],
    schema: readonly string[],
): string[] {
    return sameFieldOrder(current, prevSchema) ? [...schema] : mergeColumnOrder(current, schema);
}
